/**
 * S6 search — the shell's half (seam S6, decision D-5).
 *
 * Ranking and entitlement filtering are deliberately NOT here. A client that
 * filtered would already have been sent the thing it is hiding, and "search
 * never reveals a target the person cannot open" (BR-3) has to be true of the
 * wire, not of the render. So this file holds only what the shell adds: the
 * trail that teaches the IA, the empty-query state, and the fetch.
 */

import { API_BASE } from "./api";
import type { MeResponse } from "./api";

export type SearchKind = "area" | "module" | "tab";

export interface SearchHit {
  kind: SearchKind;
  key: string;
  display_name: string;
  area_key: string;
  area_display_name: string;
  module_key: string | null;
  /** The owning module's display name — present on a tab hit, so the trail can name it. */
  module_display_name: string | null;
  tab_key: string | null;
}

/** How many suggestions the empty-query state offers before it stops helping. */
const RECENT_LIMIT = 8;

/**
 * The trail under a result — where this thing lives.
 *
 * A bare tab name is the one result you cannot act on with confidence: "Search
 * Terms" could be in any of three modules. Naming the area and the module is
 * what makes the panel teach the IA rather than just answer the query.
 */
export function describeHit(hit: SearchHit): string {
  if (hit.kind === "area") return "Area";
  if (hit.kind === "module") return hit.area_display_name;
  return `${hit.area_display_name} · ${hit.module_display_name ?? hit.module_key ?? ""}`.trim();
}

/**
 * The empty-query suggestions, built from the nav tree the shell already holds.
 *
 * No server round trip: an empty search box must not be the most expensive
 * query in the system. These are the person's own modules, so the suggestion
 * list is entitlement-correct for free.
 *
 * Genuine "recently opened" needs a per-person history the control plane does
 * not keep yet — deliberate debt, not an oversight. Offering their modules is
 * honest about being a starting point rather than a memory.
 */
export function recentFallback(me: MeResponse): SearchHit[] {
  const hits: SearchHit[] = [];
  for (const area of me.areas) {
    for (const module of area.modules) {
      hits.push({
        kind: "module",
        key: module.module_key,
        display_name: module.display_name,
        area_key: area.area_key,
        area_display_name: area.display_name,
        module_key: module.module_key,
        module_display_name: module.display_name,
        tab_key: null,
      });
    }
  }
  return hits.slice(0, RECENT_LIMIT);
}

/** The route a hit opens. One place, so a result and the rail agree on the URL. */
export function hitPath(hit: SearchHit): string {
  if (hit.kind === "area") return `/app/${hit.area_key}`;
  if (hit.kind === "module") return `/app/${hit.area_key}/${hit.module_key}`;
  return `/app/${hit.area_key}/${hit.module_key}/${hit.tab_key}`;
}

/**
 * Query the control API.
 *
 * Never throws: a search that failed is an empty panel with a stated reason,
 * not an exception that takes the shell down around it.
 */
export async function searchTargets(
  query: string,
  signal?: AbortSignal,
): Promise<{ results: SearchHit[]; failed: boolean }> {
  if (!query.trim()) return { results: [], failed: false };
  try {
    const res = await fetch(`${API_BASE}/v1/search?q=${encodeURIComponent(query)}`, {
      credentials: "include",
      signal,
    });
    if (!res.ok) return { results: [], failed: true };
    const body = (await res.json()) as { results?: SearchHit[] };
    return { results: body.results ?? [], failed: false };
  } catch {
    return { results: [], failed: true };
  }
}
